import { RewardType } from '@/types';
import { SabaCoinRedemptionStatus } from '@/types/constants';
import { addCommas } from '@/utils';

import { useReward } from '@/contexts/reward-context';
import { ArrowRightIcon } from '@/components/svg-icons';
import { SabaCoinWithIcon } from '@/components/saba-coin-with-icon';
import { ExperienceProgressBar } from '../experience-progress-bar';
import { ThresholdButton } from '../threshold-button';
import { RedeemButton } from '../redeem-button';

interface ThresholdItemProps {
	achievedThresholdId: number;
	thresholdId: number;
	thresholdAmount: number;
	previousTurnOver: number;
	redeemablePrice: number;
	requiredCoins: number;
	isRedeemed: boolean;
	weeklyBettingAmount: number;
}

export const ThresholdItem = ({
	achievedThresholdId,
	thresholdId,
	thresholdAmount,
	previousTurnOver,
	redeemablePrice,
	requiredCoins,
	isRedeemed,
	weeklyBettingAmount
}: ThresholdItemProps) => {
	const { labels, goToEvent, currencyName } = useReward();
	const { onKafkaSet } = useReward();

	let redemptionStatus = SabaCoinRedemptionStatus.NotAchieved;
	if (isRedeemed) {
		redemptionStatus = SabaCoinRedemptionStatus.Redeemed;
	} else if (thresholdId <= achievedThresholdId) {
		redemptionStatus = SabaCoinRedemptionStatus.AchievedButNotRedeemed;
	}

	const statusText = redemptionStatus !== SabaCoinRedemptionStatus.NotAchieved 
		? labels?.lbl_SBCAchieved 
		: labels?.lbl_SBCNotAchieved;
	const redeemablePriceText = addCommas(Math.round(redeemablePrice * 1000) / 1000);

	const handleCompleteClick = () => {
		onKafkaSet({
			type: 3,
			action: 27
		});
		goToEvent?.(RewardType.Betting, 1, false);
	};

	return (
		<div 
			className="shop-coin-threshold"
			data-status={{
				[SabaCoinRedemptionStatus.NotAchieved]: 'default',
				[SabaCoinRedemptionStatus.AchievedButNotRedeemed]: 'active',
				[SabaCoinRedemptionStatus.Redeemed]: 'finish'
			}[redemptionStatus]}
		>
			<div className="shop-coin-threshold__tag">
				<div className="shop-coin-threshold__tag-text">
					{labels?.lbl_SBCThreshold?.replace('{X}', String(thresholdId))}
					-
					{statusText}
				</div>
			</div>
			
			{redemptionStatus !== SabaCoinRedemptionStatus.AchievedButNotRedeemed ? (
				<SabaCoinWithIcon coin={requiredCoins} />
			) : null}

			<div className="shop-coin-threshold__content">
				<div className="shop-coin-threshold__img">
					<div className={`shop-coin-level shop-coin-level--${thresholdId}`}></div>
				</div>
				<div className="shop-coin-threshold__info">
					<div className="shop-coin-threshold__title">
						{`${labels?.lbl_RPExchange} ${redeemablePriceText} ${currencyName}`}
					</div>
					<div className="shop-coin-threshold__desc">
						{labels?.lbl_RPSabaCoins}

						{previousTurnOver > 0 && previousTurnOver !== thresholdAmount
							? (
								<span 
									dangerouslySetInnerHTML={{ 
										__html: (labels?.lbl_RPCumulativeTurnover ?? '').replace('{X}', `
										<span class="shop-coin-threshold__level-old">${addCommas(previousTurnOver)}</span>
										<span class="shop-coin-threshold__level-new">${addCommas(thresholdAmount)}</span>
									`) 
									}}
								/>
							) 
							: labels?.lbl_RPCumulativeTurnover?.replace('{X}', addCommas(thresholdAmount))
						}
					</div>
					<ExperienceProgressBar 
						currentValue={weeklyBettingAmount}
						totalValue={thresholdAmount}
					/>
				</div>
			</div>

			{{
				[SabaCoinRedemptionStatus.NotAchieved]: (
					<ThresholdButton onClick={handleCompleteClick}>
						<span className="shop-coin-threshold__btn-text">{labels?.lbl_RPGo}</span>
						<ArrowRightIcon />
					</ThresholdButton>
				),
				[SabaCoinRedemptionStatus.AchievedButNotRedeemed]: (
					<RedeemButton 
						thresholdId={thresholdId}
						redeemablePrice={redeemablePrice}
						requiredCoins={requiredCoins}
					/>
				),
				[SabaCoinRedemptionStatus.Redeemed]: (
					<ThresholdButton isDisabled={true}>
						<span className="shop-coin-threshold__btn-text">已兌換</span>
					</ThresholdButton>
				)
			}[redemptionStatus]}
		</div>
	);
};